/**
 * Чистый редьюсер ордеров: ни React, ни сети, ни таймеров.
 *
 * Разделение то же, что у стакана (book.ts / useBooks.ts), и по той же причине:
 * правила слияния — самое дорогое место экрана, и они обязаны проверяться
 * тестом без jsdom и без подмены fetch.
 *
 * Главное правило файла — ТЕРМИНАЛЬНЫЙ СТАТУС НЕОБРАТИМ. Гидрация из REST и
 * живой поток по сокету гоняются наперегонки, и без этого правила гидрация,
 * успевшая раньше события отмены, воскрешала бы снятый ордер как живой. Оператор,
 * увидевший живым то, что снято, повторно ордер не выставит — направление ошибки
 * дорогое.
 */

import type { CommandResult, Order, OrderStatus, Side } from "../api/types";
import type { CommandSource } from "../lib/latency";
import { parse, type Micro } from "../lib/money";

/** Сколько закрытых ордеров держим. Открытые не вытесняются никогда. */
export const CLOSED_KEEP = 50;

/** Сколько ждать command_result, прежде чем признать команду «без ответа». */
export const PENDING_TIMEOUT_MS = 15_000;

/**
 * Через сколько перестать ждать ответа СОВСЕМ и забыть команду.
 *
 * Второй рубеж нужен потому, что первый ничего не заканчивает: пометка
 * «без ответа» вечна, а удаляет запись только пришедший command_result. Ответ
 * же может не прийти никогда по совершенно штатной причине — web_api
 * переигрывает новому сокету историю ТОЛЬКО evt.tape, и вердикт, выпавший в
 * разрыв связи, до вкладки не доедет уже ничем. Без этого рубежа один разрыв
 * оставлял бы счётчик «команд в полёте» ненулевым и предупреждение о застрявшей
 * команде — на экране до перезагрузки страницы.
 *
 * Забывание команды НЕ означает «ордера нет»: строка ордера остаётся, но с этого
 * момента ею распоряжается гидрация, то есть источник истины — трейдер. Это и
 * есть честный ответ на «судьба неизвестна», в отличие от местной догадки,
 * застывшей навсегда.
 */
export const PENDING_GIVEUP_MS = 60_000;

/** Статусы, из которых ордер не возвращается. Зеркало storage.IsTerminal. */
const TERMINAL: ReadonlySet<OrderStatus> = new Set<OrderStatus>([
  "MATCHED",
  "CANCELED",
  "UNMATCHED",
  "REJECTED",
  // Без него снятому биржей ордеру панель рисовала кнопку «отмена»: Go-сторона
  // знает статус с 2026-08-25, а это зеркало тогда не поправили.
  "CANCELED_MARKET_RESOLVED",
  "INVALID",
]);

export function isTerminal(s: OrderStatus): boolean {
  return TERMINAL.has(s);
}

/**
 * Ранг статуса — тай-брейк, когда отметки времени совпали.
 *
 * updated_at — часы ТРЕЙДЕРА, и совпадения в пределах миллисекунды реальны:
 * подтверждение постановки и первое событие приватного канала приходят почти
 * одновременно. Без ранга порядок в этот момент определялся бы случайностью.
 */
function rank(s: OrderStatus): number {
  if (isTerminal(s)) return 3;
  switch (s) {
    case "PENDING":
      return 0;
    case "DELAYED":
      return 1;
    default:
      return 2; // LIVE
  }
}

/**
 * Что за команда «в полёте».
 *
 * `place_paper` отдельно от `place`, а не флагом рядом: по этому виду решается,
 * заводить ли ОПТИМИСТИЧНУЮ строку в списке ордеров. Пробный налив в `orders`
 * не живёт вовсе (он в своей таблице), и строка-призрак там означала бы ордер,
 * которого на бирже нет и не будет, — то есть ровно то, ради недопущения чего
 * список и существует.
 */
export type PendingKind = "place" | "place_paper" | "cancel" | "cancel_all";

/** Команда «в полёте»: отправлена, ответа ещё нет. */
export interface Pending {
  traceId: string;
  kind: PendingKind;
  /** Ордер, которого команда касается. Пусто у cancel_all. */
  clientOrderId: string;
  /**
   * ЧАСЫ БРАУЗЕРА (Date.now). Для таймаута и только для него.
   *
   * ВНИМАНИЕ: это НЕ момент клика — отметка ставится после того, как вернулся
   * ответ 202, то есть уже с сетевым кругом внутри. Для измерения задержки она
   * непригодна, для этого есть clickedAtMono ниже.
   */
  startedAt: number;
  /**
   * ЧАСЫ БРАУЗЕРА, МОНОТОННЫЕ (performance.now) — момент КЛИКА.
   *
   * Отдельно от startedAt по двум причинам сразу: другая точка отсчёта и другие
   * часы. Date.now умеет прыгнуть от коррекции NTP прямо посреди замера, и для
   * таймаута в пятнадцать секунд это безразлично, а для задержки в двести
   * миллисекунд — нет. Тот же приём, что у AssetBook.snapshotAt/appliedAt.
   */
  clickedAtMono: number;
  /** ЧАСЫ БРАУЗЕРА, МОНОТОННЫЕ — момент получения 202. */
  acceptedAtMono: number | null;
  /** Откуда пришла команда: форма, кнопка панели или клавиша. */
  source: CommandSource;
  error: string | null;
  timedOut: boolean;
}

/** Пришедший вердикт по команде вместе с моментом получения (часы браузера). */
export interface Verdict {
  res: CommandResult;
  at: number;
}

export interface OrdersState {
  /**
   * client_order_id -> ордер. Ключ именно он: order_id биржа присваивает позже,
   * и карта по нему завела бы ВТОРУЮ строку на тот же ордер в окне между
   * PENDING и LIVE. Ровно поэтому же он первичный ключ в таблице.
   */
  orders: Record<string, Order>;
  /** trace_id -> команда в полёте. */
  pending: Record<string, Pending>;
  /**
   * trace_id -> пришедший вердикт. Служит ДВУМ разным потребителям.
   *
   * Первый — память о ГОНКЕ. Вердикт приезжает по сокету, который уже открыт, а
   * команда заводится «в полёте» только после ответа 202 на POST: два
   * независимых канала, и порядок между ними ничем не задан. Обгон реален —
   * достаточно, чтобы доставку 202 задержал буферизующий прокси или
   * ретрансмиссия TCP. Без этой отметки обогнанная запись легла бы в pending
   * ПОСЛЕ своего единственного ответа и не удалилась бы уже никогда: успешно
   * выполненная команда вечно числилась бы застрявшей.
   *
   * Второй — тот, кто отправил команду и хочет знать, чем она кончилась.
   * Строка ордера на этот вопрос отвечает не всегда: у cancel_all её нет вовсе,
   * а отказ гейта до биржи не доходит. Вердикт же есть у любой команды, и
   * ключуется он тем же trace_id, каким она отправлялась.
   *
   * Живут записи не дольше PENDING_GIVEUP_MS: дальше держать их незачем, а
   * расти без предела на вкладке, открытой сутками, они не имеют права.
   */
  verdicts: Record<string, Verdict>;
  hydratedAt: number;
  hydrating: boolean;
  hydrateError: string | null;
}

export function emptyOrders(): OrdersState {
  return {
    orders: {},
    pending: {},
    verdicts: {},
    hydratedAt: 0,
    hydrating: false,
    hydrateError: null,
  };
}

/**
 * Слияние двух представлений одного ордера.
 *
 * Идемпотентно ПО ПОСТРОЕНИЮ, а не по набору виденных идентификаторов — по той
 * же причине, по которой абсолютный размер уровня делает безвредным повтор
 * дельты стакана. Набор stream_id в dispatch.ts закрывает границу «история ∩
 * живой поток», но гонку REST-гидрации с сокетом он не закрывает; её закрывают
 * правила ниже.
 */
export function mergeOrder(prev: Order | undefined, next: Order): Order {
  if (!prev) return next;

  // 1. Терминальный статус необратим.
  const status = isTerminal(prev.status) ? prev.status : pickStatus(prev, next);
  // 2. Исполнение только растёт: пришедшее меньшее значение — это отставшее
  //    событие, а не частичная отмена.
  const matched = maxAmount(prev.size_matched, next.size_matched);

  const winner = newer(prev, next);
  // order_id и причина отказа не теряются: биржа присваивает первый позже, а
  // вторую заполняет только один из источников. Условный спред, а не
  // присваивание undefined: при exactOptionalPropertyTypes «свойства нет» и
  // «свойство равно undefined» — разные вещи, и второе тип Order не принимает.
  const orderId = prev.order_id || next.order_id;
  const reason = winner.reason || prev.reason || next.reason;
  // Аккаунт у ордера один и не меняется, а знают его не все источники:
  // обновление исполнителя до 2026-10-07 приезжало без него, и строка отказа,
  // слитая по свежести, теряла аккаунт оптимистичной строки («—» в столбце).
  const account = winner.account || prev.account || next.account;
  return {
    ...winner,
    status,
    size_matched: matched,
    ...(orderId ? { order_id: orderId } : {}),
    ...(reason ? { reason } : {}),
    ...(account ? { account } : {}),
  };
}

function pickStatus(prev: Order, next: Order): OrderStatus {
  return newer(prev, next).status;
}

/** Более свежее представление: по updated_at, при равенстве — по рангу статуса. */
function newer(prev: Order, next: Order): Order {
  const a = Date.parse(prev.updated_at || "");
  const b = Date.parse(next.updated_at || "");
  if (Number.isFinite(a) && Number.isFinite(b) && a !== b) return b > a ? next : prev;
  return rank(next.status) >= rank(prev.status) ? next : prev;
}

function maxAmount(a: string, b: string): string {
  const x = parse(a);
  const y = parse(b);
  if (x === null) return b;
  if (y === null) return a;
  return y > x ? b : a;
}

/** Применяет обновление ордера из потока. */
export function ingestOrder(state: OrdersState, order: Order): OrdersState {
  const key = order.client_order_id;
  if (!key) return state;

  const merged = mergeOrder(state.orders[key], order);
  if (merged === state.orders[key]) return state;

  return prune({ ...state, orders: { ...state.orders, [key]: merged } });
}

/**
 * Заменяет открытое множество результатом гидрации.
 *
 * ЗАМЕНЯЕТ, а не дополняет: источник истины по ордерам — трейдер, и локально
 * открытый ордер, которого нет в ответе, обязан из открытых исчезнуть.
 * Фантомный «живой» ордер — опасное направление ошибки: оператор считает, что
 * позиция есть, и не выставляет её заново. Обратное направление подстраховано
 * тем, что «снять все» доступно всегда и не выводится из содержимого списка.
 *
 * Терминальные строки при этом сохраняются: они уже не открыты, а история
 * последних действий на экране нужна.
 */
export function hydrateOpen(state: OrdersState, fresh: Order[], now: number): OrdersState {
  const orders: Record<string, Order> = {};

  // Команды, ответа на которые ещё нет. Их строки гидрация НЕ удаляет, даже
  // если сервер о них пока не знает: пока команда в полёте, источник истины о
  // самом факте её существования — мы, а не журнал. Трейдер заводит строку не
  // мгновенно, и без этого исключения ордер, который оператор только что
  // отправил, исчезал бы с экрана на время гонки гидрации с записью — а это тот
  // самый момент, когда человек тянется отправить его повторно.
  //
  // Щит снимается, как только команда признана «без ответа». Он прикрывает
  // ГОНКУ длиной в доли секунды, а не незнание длиной в минуты: за
  // PENDING_TIMEOUT_MS трейдер успел бы записать строку тысячу раз, и дальше
  // оптимистичная строка перестаёт быть опережением и становится вымыслом —
  // вечно живым PENDING-ордером, которого у трейдера нет (например, потому что
  // риск-гейт его отклонил, а вердикт выпал в разрыв сокета).
  const inFlight = new Set(
    Object.values(state.pending)
      .filter((p) => !p.timedOut)
      .map((p) => p.clientOrderId)
      .filter(Boolean),
  );

  // Сначала терминальные из прежнего состояния — их гидрация не приносит.
  for (const [key, order] of Object.entries(state.orders)) {
    if (isTerminal(order.status) || inFlight.has(key)) orders[key] = order;
  }
  for (const order of fresh) {
    const key = order.client_order_id;
    if (!key) continue;
    orders[key] = mergeOrder(state.orders[key], order);
  }

  return prune({ ...state, orders, hydratedAt: now, hydrating: false, hydrateError: null });
}

/** Полная гидрация (status=all) — тот же путь, но без сохранения прежних. */
export function hydrateAll(state: OrdersState, fresh: Order[], now: number): OrdersState {
  const orders: Record<string, Order> = {};
  for (const order of fresh) {
    const key = order.client_order_id;
    if (!key) continue;
    orders[key] = mergeOrder(state.orders[key], order);
  }
  return prune({ ...state, orders, hydratedAt: now, hydrating: false, hydrateError: null });
}

/** Вытеснение закрытых. Открытые не вытесняются никогда. */
function prune(state: OrdersState): OrdersState {
  const closed = Object.values(state.orders).filter((o) => isTerminal(o.status));
  if (closed.length <= CLOSED_KEEP) return state;

  closed.sort((a, b) => Date.parse(b.updated_at || "") - Date.parse(a.updated_at || ""));
  const drop = new Set(closed.slice(CLOSED_KEEP).map((o) => o.client_order_id));

  const orders: Record<string, Order> = {};
  for (const [key, order] of Object.entries(state.orders)) {
    if (!drop.has(key)) orders[key] = order;
  }
  return { ...state, orders };
}

// ---------------------------------------------------------------------------
// Команды в полёте
// ---------------------------------------------------------------------------

export function startPending(
  state: OrdersState,
  p: Omit<Pending, "error" | "timedOut">,
): OrdersState {
  // Вердикт уже приходил — заводить команду «в полёте» поздно и вредно: свой
  // единственный command_result она получила ДО того, как о ней узнали здесь,
  // и второго не будет (см. OrdersState.verdicts).
  //
  // Запись при этом НЕ удаляется: её ждёт тот, кто отправил команду и хочет
  // показать оператору, чем она кончилась. Уборка — по сроку, в expirePending.
  if (state.verdicts[p.traceId] !== undefined) {
    return state;
  }
  return {
    ...state,
    pending: { ...state.pending, [p.traceId]: { ...p, error: null, timedOut: false } },
  };
}

/**
 * Оптимистичная строка ордера с учётом уже пришедшего вердикта.
 *
 * Отказ трейдера бывает быстрее ответа web_api на саму команду: риск-гейт
 * отвечает за миллисекунды, и command_result по сокету обгоняет 202 по HTTP.
 * Вердикт тогда уже лежит в verdicts, а строки ещё нет, — и строка, заведённая
 * после, висела PENDING с кнопкой отмены до перезагрузки вкладки (прод
 * 2026-10-08: продажа A03 post-only, отказ за 4 мс). Второго вердикта не
 * будет, поэтому применить его можно только здесь — тем же правилом, что
 * ingestResult.
 */
export function addOptimistic(state: OrdersState, row: Order, traceId: string): OrdersState {
  const v = traceId ? state.verdicts[traceId] : undefined;
  if (!v || v.res.accepted) return ingestOrder(state, row);
  const reason = v.res.error || row.reason;
  if (v.res.unresolved) {
    // Судьба неизвестна — строка остаётся PENDING, но с причиной (см.
    // ingestResult): «завис» не должен выглядеть молчанием интерфейса.
    return ingestOrder(state, { ...row, ...(reason ? { reason } : {}) });
  }
  return ingestOrder(state, {
    ...row,
    status: "REJECTED",
    ...(reason ? { reason } : {}),
    updated_at: new Date(v.at).toISOString(),
  });
}

/**
 * Применяет command_result.
 *
 * Отказ НЕ удаляет строку ордера, а помечает её REJECTED с текстом трейдера:
 * исчезнувшая строка не объясняет оператору, почему ордера нет.
 *
 * «Судьба неизвестна» (unresolved) — НЕ отказ: трейдер не получил ответа биржи,
 * ордер остался PENDING, и его судьбу выясняет сверка. Пометить его REJECTED
 * значило бы соврать необратимо: терминальный статус не отменяется, живой ордер
 * исчез бы из открытых, и оператор поставил бы второй на те же деньги.
 */
export function ingestResult(
  state: OrdersState,
  traceId: string,
  res: CommandResult,
  now: number,
): OrdersState {
  const pending = state.pending[traceId];
  const next: OrdersState = { ...state, pending: { ...state.pending } };
  delete next.pending[traceId];
  if (traceId) {
    // Вердикт запоминается ВСЕГДА, а не только когда он обогнал свою команду:
    // им же отвечают отправителю, чем кончилось (см. OrdersState.verdicts).
    next.verdicts = { ...next.verdicts, [traceId]: { res, at: now } };
  }

  if (res.accepted) return next;

  const key = res.client_order_id || pending?.clientOrderId || "";
  const existing = key ? next.orders[key] : undefined;
  if (!existing) return next;
  // Отклонённый ордер до биржи не доехал: терминальность здесь обязана
  // перебить локальный PENDING, поэтому статус ставится напрямую.
  if (isTerminal(existing.status)) return next;

  const reason = res.error || existing.reason;
  if (res.unresolved) {
    // Строка остаётся PENDING; текст причины показывается, чтобы «ордер
    // завис» не выглядел как молчание интерфейса.
    next.orders = {
      ...next.orders,
      [key]: { ...existing, ...(reason ? { reason } : {}) },
    };
    return next;
  }

  next.orders = {
    ...next.orders,
    [key]: {
      ...existing,
      status: "REJECTED",
      ...(reason ? { reason } : {}),
      updated_at: new Date(now).toISOString(),
    },
  };
  return next;
}

/**
 * Ведёт срок жизни команд без ответа — в ДВА рубежа.
 *
 * PENDING_TIMEOUT_MS — «ответа всё ещё нет»: запись остаётся, оператор видит
 * предупреждение. Строка ордера при этом ОСТАЁТСЯ в PENDING и не удаляется:
 * команда durable в стриме cmd.orders и будет выполнена, когда трейдер
 * вернётся. Сказать оператору «ордер не выставлен» значило бы спровоцировать
 * второй.
 *
 * PENDING_GIVEUP_MS — «ждать больше нечего»: запись удаляется. Одной пометки
 * мало, потому что она ничем не заканчивается: единственный, кто удалял запись,
 * — пришедший command_result, а он после разрыва сокета не придёт уже никогда
 * (истории evt.commands новому клиенту никто не переигрывает). Молчаливого
 * обмана здесь нет: с этого момента строкой ордера распоряжается гидрация,
 * которая спрашивает трейдера, вместо местной догадки, застывшей навсегда.
 */
export function expirePending(state: OrdersState, now: number): OrdersState {
  let changed = false;
  const pending: Record<string, Pending> = {};
  for (const [traceId, p] of Object.entries(state.pending)) {
    if (now - p.startedAt >= PENDING_GIVEUP_MS) {
      changed = true;
      continue;
    }
    if (!p.timedOut && now - p.startedAt >= PENDING_TIMEOUT_MS) {
      pending[traceId] = { ...p, timedOut: true };
      changed = true;
      continue;
    }
    pending[traceId] = p;
  }

  const verdicts: Record<string, Verdict> = {};
  for (const [traceId, v] of Object.entries(state.verdicts)) {
    if (now - v.at >= PENDING_GIVEUP_MS) {
      changed = true;
      continue;
    }
    verdicts[traceId] = v;
  }

  return changed ? { ...state, pending, verdicts } : state;
}

// ---------------------------------------------------------------------------
// Отметка своих уровней в стакане
// ---------------------------------------------------------------------------

/** Наш след на одном уровне стакана. */
export interface OwnMark {
  /** Сколько нашего осталось неисполненным на этом уровне. */
  remaining: Micro;
  /** Есть ли среди них ордер, ещё не подтверждённый биржей. */
  pending: boolean;
  count: number;
}

/**
 * Отметка собственных ордеров на уровнях стакана.
 *
 * Владения в стакане нет и быть не может: биржа присылает АГРЕГИРОВАННЫЕ
 * уровни. Отметка ставится сопоставлением наших открытых ордеров по
 * (asset_id, сторона, цена), и это ЭВРИСТИКА, а не факт. Отсюда два правила
 * оформления, оба обязательные (см. BookLadder):
 *
 *   - размер уровня НЕ уменьшается на наш: уровень общий, и «300, из них ваши
 *     100» честно, а «200» — вымысел;
 *   - ордер в статусе PENDING отмечается ПОЛЫМ маркером. Он ещё не на бирже, и
 *     сплошной маркер за секунду до попадания в стакан обещал бы оператору то,
 *     чего пока нет, — ровно в том сценарии, который эта веха и принимает.
 *
 * Сопоставление идёт по МИКРО-единицам, а не по строкам: с провода приходит
 * «0.5», а оператор ввёл «0.50».
 */
export function ownLevels(
  orders: Record<string, Order>,
  assetId: string,
): { bids: Map<Micro, OwnMark>; asks: Map<Micro, OwnMark> } {
  const bids = new Map<Micro, OwnMark>();
  const asks = new Map<Micro, OwnMark>();

  for (const order of Object.values(orders)) {
    if (order.asset_id !== assetId || isTerminal(order.status)) continue;

    const price = parse(order.price);
    const size = parse(order.size);
    const matched = parse(order.size_matched);
    if (price === null || size === null) continue;

    const remaining = size - (matched ?? 0);
    if (remaining <= 0) continue;

    // BUY нашего ордера стоит на bid этого же токена, SELL — на ask.
    const side: Side = order.side;
    const target = side === "BUY" ? bids : asks;
    const prev = target.get(price);
    target.set(price, {
      remaining: (prev?.remaining ?? 0) + remaining,
      pending: (prev?.pending ?? false) || order.status === "PENDING",
      count: (prev?.count ?? 0) + 1,
    });
  }

  return { bids, asks };
}

/** Открытые ордера, отсортированные для панели: свежие сверху. */
export function openOrders(state: OrdersState, conditionId?: string | null): Order[] {
  return Object.values(state.orders)
    .filter((o) => !isTerminal(o.status))
    .filter((o) => !conditionId || o.condition_id === conditionId)
    .sort((a, b) => Date.parse(b.created_at || "") - Date.parse(a.created_at || ""));
}

/** Недавно закрытые — для той же панели, ниже открытых. */
export function closedOrders(state: OrdersState, conditionId?: string | null): Order[] {
  return Object.values(state.orders)
    .filter((o) => isTerminal(o.status))
    .filter((o) => !conditionId || o.condition_id === conditionId)
    .sort((a, b) => Date.parse(b.updated_at || "") - Date.parse(a.updated_at || ""));
}

/**
 * Все ордера ОДНОГО рынка: выборка журнала по рынку плюс живой стор.
 *
 * Стора одного не хватает: он держит CLOSED_KEEP закрытых по ВСЕМ рынкам, и на
 * окне, где стратегия ставила ордер за ордером, ранние из них вытесняются
 * соседними окнами раньше, чем окно кончится. Журнала одного тоже: он
 * спрашивается опросом, а свежий ордер и его исполнение приходят по сокету
 * сразу. Слияние — тем же mergeOrder, что у гидрации, поэтому терминальный
 * статус и здесь необратим, а исполнение только растёт.
 */
export function marketOrders(
  history: readonly Order[],
  live: Readonly<Record<string, Order>>,
  conditionId: string,
): Order[] {
  const out = new Map<string, Order>();
  for (const order of history) {
    if (order.condition_id !== conditionId || !order.client_order_id) continue;
    out.set(order.client_order_id, order);
  }
  for (const order of Object.values(live)) {
    if (order.condition_id !== conditionId || !order.client_order_id) continue;
    out.set(order.client_order_id, mergeOrder(out.get(order.client_order_id), order));
  }
  return [...out.values()].sort(
    (a, b) => Date.parse(a.created_at || "") - Date.parse(b.created_at || ""),
  );
}
